/**
 * New Mexico Home Health Agency State-Specific Diagnostic Questions
 * 
 * REGULATORY FRAMEWORK (Updated July 2024):
 * - Primary Regulation: 8.370.22 NMAC (Requirements for Home Health Agencies)
 *   REPLACES: 7.28.2 NMAC (repealed effective 7/1/2024)
 * - Statutory Authority: NMSA 1978 § 24-1-2, § 24-1-3, § 24-1-5 (Public Health Act)
 *   and § 9-8-6 (Health Care Authority Act)
 * - Caregiver Screening: 8.370.5 NMAC (Criminal History Screening)
 * - Employee Abuse Registry: 8.370.8 NMAC
 * - Incident Management: 8.370.9 NMAC
 * - Licensure Fees: 8.370.3 NMAC
 * - Federal Standards: 42 CFR 484 (Medicare CoPs - for certified agencies)
 * 
 * REGULATORY BODY:
 * New Mexico Health Care Authority (HCA)
 * Division of Health Improvement (DHI)
 * Health Facility Licensing and Certification Bureau
 * Hotline: 1-800-752-8649 (8am-5pm M-F)
 * Website: https://www.hca.nm.gov/division-of-health-improvement/
 * 
 * KEY VERIFIED REQUIREMENTS (December 2024):
 * - License: Annual (1 year), non-transferable
 * - Record retention: 10 years after discharge
 * - HHA Training: 75 hours (16 supervised practical)
 * - HHA Annual in-service: 12 hours
 * - PCA/Homemaker Training: 40 hours first year, 10 hours annual
 * - HHA Supervision: Every 62 days minimum
 * - Administrator: Physician, RN, OR HS diploma + 1 year supervisory experience
 * - POC submission: 10 working days after survey
 * - Complaint investigation: Within 3 working days
 * - Incident documentation: Within 5 days
 * - Service area: 100 miles from licensed location
 * - Branch office: Must be within 100 miles of parent, parent must be 1+ year old
 * 
 * Last Verified: December 2024 against 8.370.22 NMAC (effective 7/1/2024)
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const NEWMEXICO_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'NM-A1',
    itemId: 'A1',
    stateCode: 'NM',
    title: 'HCA Home Health Agency License Status',
    description: 'New Mexico requires all home health agencies to be licensed by the Health Care Authority under 8.370.22 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.10-12',
    regulationName: 'Licensure',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Health Agency license from the NM Health Care Authority?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 8.370.22.11 NMAC: Annual license issued for one year to agencies meeting all requirements.',
      },
      {
        id: 'q2',
        question: 'What is your license expiration date?',
        inputType: 'date',
        helpText: 'Per 8.370.22.12 NMAC: Renewal application must be submitted at least 30 days prior to expiration.',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 30, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your NM Health Care Authority Home Health Agency license is current per 8.370.22 NMAC.',
      atRisk: 'Your license expires within 90 days. Per 8.370.22.12 NMAC, submit renewal at least 30 days before expiration.',
      nonCompliant: 'Operating without a valid license violates NMSA 1978 § 24-1-5 and 8.370.22 NMAC. Contact DHI at 1-800-752-8649.',
      unknown: 'Verify your license status with DHI at 1-800-752-8649.',
    },
    stateNotes: [
      'Annual license valid for 1 year per 8.370.22.11(A)',
      'Temporary license: up to 120 days, max 2 consecutive per 8.370.22.11(B)',
      'Submit renewal 30+ days before expiration per 8.370.22.12(A)',
      'License non-transferable per 8.370.22.14',
    ],
  },

  {
    id: 'NM-A2',
    itemId: 'A2',
    stateCode: 'NM',
    title: 'Administrator Qualifications',
    description: 'New Mexico requires qualified administrator per 8.370.22.26 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.26',
    regulationName: 'Administrator',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Does your administrator meet ONE of the following 8.370.22.26 NMAC requirements?',
        inputType: 'select',
        options: [
          { value: 'physician', label: 'Licensed physician' },
          { value: 'rn', label: 'Registered nurse' },
          { value: 'qualified', label: 'HS diploma/GED + health services admin training + 1 year supervisory/admin experience in home health' },
          { value: 'none', label: 'Does not meet any requirement' },
        ],
        required: true,
        helpText: 'Per 8.370.22.26 NMAC: Administrator must be physician, RN, OR have HS diploma with training and 1 year experience.',
      },
      {
        id: 'q2',
        question: 'Is there a qualified person authorized in writing to act in the administrator\'s absence?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Required per 8.370.22.26(F) NMAC.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'none', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['physician', 'rn', 'qualified'] },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Administrator meets 8.370.22.26 NMAC qualifications with designated backup.',
      atRisk: 'Designate a qualified person in writing to act in administrator\'s absence per 8.370.22.26(F).',
      nonCompliant: 'Administrator does not meet 8.370.22.26 NMAC requirements.',
      unknown: 'Verify administrator qualifications against 8.370.22.26 NMAC.',
    },
    stateNotes: [
      'Administrator appointed by governing body per 8.370.22.26',
      'May also serve as supervising physician or RN per 8.370.22.26(D)',
      'Responsible for implementing governing body directions per 8.370.22.26(E)',
      'Written backup authorization required per 8.370.22.26(F)',
    ],
  },

  {
    id: 'NM-A3',
    itemId: 'A3',
    stateCode: 'NM',
    title: 'Governing Body Requirements',
    description: 'New Mexico requires governing body with specific responsibilities per 8.370.22.24 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.24',
    regulationName: 'Governing Body',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Does your governing body meet all 8.370.22.24 NMAC requirements?',
        inputType: 'checklist',
        options: [
          { value: 'bylaws', label: 'Adopts and reviews written bylaws/policies annually' },
          { value: 'authority', label: 'Has full legal authority for agency operation' },
          { value: 'administrator', label: 'Appoints qualified administrator' },
          { value: 'fiscal', label: 'Oversees management and fiscal affairs' },
          { value: 'meetings', label: 'Meets at least annually with documented minutes' },
        ],
        required: true,
        helpText: 'Per 8.370.22.24 NMAC: Governing body must meet all listed requirements.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Governing body meets all 8.370.22.24 NMAC requirements.',
      atRisk: 'Governing body is missing some required functions.',
      nonCompliant: 'Governing body does not meet 8.370.22.24 NMAC requirements.',
      unknown: 'Verify governing body compliance with 8.370.22.24 NMAC.',
    },
    stateNotes: [
      'Governing body may include licensee per 8.370.22.24(A)',
      'Must have full legal authority per 8.370.22.24(B)',
      'Annual meetings with documented minutes required per 8.370.22.24(E)',
    ],
  },

  {
    id: 'NM-A4',
    itemId: 'A4',
    stateCode: 'NM',
    title: 'Advisory Group Requirements',
    description: 'New Mexico requires advisory group per 8.370.22.25 NMAC',
    severity: 'medium',
    regulationCode: '8.370.22.25',
    regulationName: 'Advisory Group',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Does your advisory group meet 8.370.22.25 NMAC composition requirements?',
        inputType: 'checklist',
        options: [
          { value: 'three', label: 'At least 3 individuals' },
          { value: 'service_rep', label: 'Representative of at least one service offered' },
          { value: 'non_employee', label: 'At least one member neither owner nor employee' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does the advisory group meet semi-annually and perform required functions?',
        inputType: 'checklist',
        options: [
          { value: 'semi_annual', label: 'Meets at least semi-annually' },
          { value: 'review_policies', label: 'Reviews policies/procedures and QI program annually' },
          { value: 'program_eval', label: 'Participates in annual program evaluation' },
          { value: 'professional', label: 'Advises on professional issues' },
          { value: 'liaison', label: 'Maintains liaison with community health providers' },
          { value: 'minutes', label: 'Documents meetings with dated minutes on file' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 3 },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Advisory group meets 8.370.22.25 NMAC requirements.',
      atRisk: 'Advisory group composition or functions need attention.',
      nonCompliant: 'Advisory group does not meet 8.370.22.25 NMAC requirements.',
      unknown: 'Verify advisory group compliance.',
    },
    stateNotes: [
      'Minimum 3 individuals required',
      'At least one non-owner/non-employee member required',
      'Semi-annual meetings required',
      'Documented minutes required',
    ],
  },

  {
    id: 'NM-A5',
    itemId: 'A5',
    stateCode: 'NM',
    title: 'Written Policies and Procedures',
    description: 'New Mexico requires specific written policies per 8.370.22.38 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.38',
    regulationName: 'Policies',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following required policies does your agency have documented per 8.370.22.38 NMAC?',
        inputType: 'checklist',
        options: [
          { value: 'scope', label: 'Scope of services offered - 8.370.22.38(A)' },
          { value: 'contracted', label: 'Services through arrangement/contract - 8.370.22.38(B)' },
          { value: 'admission', label: 'Admission and discharge - 8.370.22.38(C)' },
          { value: 'job_desc', label: 'Written job descriptions for all personnel - 8.370.22.38(D)' },
          { value: 'personnel', label: 'Personnel policies - 8.370.22.38(E)' },
          { value: 'training', label: 'Staff training - 8.370.22.38(F)' },
          { value: 'emergency', label: 'Emergency and after-hours care - 8.370.22.38(G)' },
          { value: 'records', label: 'Preparation, safeguarding, release of patient records - 8.370.22.38(H)' },
          { value: 'qi', label: 'Quality improvement program - 8.370.22.38(I)' },
          { value: 'complaints', label: 'Complaints and grievances with timely resolution - 8.370.22.38(J)' },
        ],
        required: true,
        helpText: 'All policies listed in 8.370.22.38 NMAC are required.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Policy manual contains all required elements per 8.370.22.38 NMAC.',
      atRisk: 'Policy manual is missing required sections.',
      nonCompliant: 'Multiple required policies missing. DHI surveyors will cite deficiencies.',
      unknown: 'Review policies against 8.370.22.38 NMAC requirements.',
    },
    stateNotes: [
      'All 10 policy areas required per 8.370.22.38 NMAC',
      'Policies reviewed during DHI surveys',
      'Must annotate policies to regulations per 8.370.22.10(B)(4)',
    ],
  },

  {
    id: 'NM-A6',
    itemId: 'A6',
    stateCode: 'NM',
    title: 'Service Area and Branch Office Compliance',
    description: 'New Mexico limits service area to 100 miles per 8.370.22.9 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.9(E)',
    regulationName: 'Service Area',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do all patients reside within 100 miles of your licensed location?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, all within 100 miles' },
          { value: 'exception', label: 'Some beyond 100 miles with DHI exception' },
          { value: 'no', label: 'No, serving beyond 100 miles without exception' },
        ],
        required: true,
        helpText: 'Per 8.370.22.9(F): Service area limited to 100 miles from licensed location.',
      },
      {
        id: 'q2',
        question: 'Do you operate branch offices?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
      {
        id: 'q3',
        question: 'Do ALL branch offices meet 8.370.22.9(E) requirements?',
        inputType: 'checklist',
        options: [
          { value: 'one_year', label: 'Parent agency in operation at least 1 year with annual survey' },
          { value: 'compliant', label: 'Parent in substantial compliance' },
          { value: 'distance', label: 'Branch within 100 miles of parent' },
          { value: 'admin', label: 'Qualified on-site branch administrator' },
          { value: 'services', label: 'Provides same services as parent' },
        ],
        showCondition: {
          questionId: 'q2',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'includes', questionId: 'q1', value: ['yes', 'exception'] },
        { condition: 'countAtLeast', questionId: 'q3', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q3', value: 3, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Service area and branch office requirements met per 8.370.22.9 NMAC.',
      atRisk: 'Branch office may not meet all requirements.',
      nonCompliant: 'Service area exceeds 100 miles without DHI exception.',
      unknown: 'Verify service area and branch office compliance.',
    },
    stateNotes: [
      'Service area: 100 miles from licensed location',
      'Branch must be within 100 miles of parent',
      'Parent must operate 1+ year before opening branch',
      'DHI may grant temporary exception if no other agency available',
    ],
  },

  {
    id: 'NM-A7',
    itemId: 'A7',
    stateCode: 'NM',
    title: 'Patient Record Retention',
    description: 'New Mexico requires 10-year record retention per 8.370.22.34(D) NMAC',
    severity: 'high',
    regulationCode: '8.370.22.34(D)',
    regulationName: 'Retention',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain original patient records after discharge?',
        inputType: 'select',
        options: [
          { value: '10plus', label: '10 years or longer (meets NM requirement)' },
          { value: '7to9', label: '7-9 years' },
          { value: '6', label: '6 years' },
          { value: 'less6', label: 'Less than 6 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Per 8.370.22.34(D)(1): Original patient records shall be retained for at least 10 years after discharge.',
      },
      {
        id: 'q2',
        question: 'If your agency discontinues operations, will records be maintained for the required period?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, plan in place per 8.370.22.34(D)(2)-(3)' },
          { value: 'no', label: 'No plan in place' },
        ],
        helpText: 'Per 8.370.22.34(D)(2)-(3): Records must be maintained even if agency discontinues; DHI must be notified of storage location.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: '10plus', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '7to9', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['6', 'less6', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets New Mexico\'s 10-year requirement per 8.370.22.34(D).',
      atRisk: 'New Mexico requires 10-year retention. Update your policy immediately.',
      nonCompliant: 'Your record retention does not meet New Mexico\'s 10-year requirement.',
      unknown: 'Review record retention requirements under 8.370.22.34(D) NMAC.',
    },
    stateNotes: [
      '10 years after discharge per 8.370.22.34(D)(1)',
      'Maintain records even if agency discontinues per 8.370.22.34(D)(2)',
      'Notify DHI of storage location before discontinuing per 8.370.22.34(D)(3)',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL & BACKGROUND SCREENING
  // ============================================
  {
    id: 'NM-B1',
    itemId: 'B1',
    stateCode: 'NM',
    title: 'Caregiver Criminal History Screening',
    description: 'New Mexico requires criminal history screening per 8.370.5 NMAC',
    severity: 'high',
    regulationCode: '8.370.5',
    regulationName: 'Background Checks',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'For ALL employees with patient contact, which background checks do you complete BEFORE patient contact?',
        inputType: 'checklist',
        options: [
          { value: 'nm_criminal', label: 'New Mexico criminal background check' },
          { value: 'fbi', label: 'FBI fingerprint background check' },
          { value: 'abuse_registry', label: 'Employee Abuse Registry check (8.370.8 NMAC)' },
          { value: 'nurse_aide', label: 'Nurse Aide Registry check (if applicable)' },
          { value: 'oig', label: 'OIG exclusion list verification' },
        ],
        required: true,
        helpText: 'Per 8.370.5 NMAC: Criminal history screening required for all caregivers.',
      },
      {
        id: 'q2',
        question: 'Do any employees begin patient contact BEFORE background check clearance?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Background screening meets 8.370.5 NMAC requirements.',
      atRisk: 'Background screening documentation may be incomplete.',
      nonCompliant: 'Staff working before background check clearance or without proper screening violates 8.370.5 NMAC.',
      unknown: 'Audit all staff files for criminal history screening documentation.',
    },
    stateNotes: [
      'Criminal history screening required per 8.370.5 NMAC',
      'Employee Abuse Registry check required per 8.370.8 NMAC',
      'Complete before patient contact',
      'Document results in personnel file',
    ],
  },

  {
    id: 'NM-B2',
    itemId: 'B2',
    stateCode: 'NM',
    title: 'Staff Record Requirements',
    description: 'New Mexico requires specific staff record elements per 8.370.22.37 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.37',
    regulationName: 'Staff Records',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Which required elements do you maintain in EVERY staff member\'s record per 8.370.22.37 NMAC?',
        inputType: 'checklist',
        options: [
          { value: 'name', label: 'Name - 8.370.22.37(A)' },
          { value: 'address', label: 'Address - 8.370.22.37(B)' },
          { value: 'position', label: 'Position employed - 8.370.22.37(C)' },
          { value: 'date', label: 'Date of employment - 8.370.22.37(D)' },
          { value: 'tb', label: 'TB health certificate per infectious disease bureau - 8.370.22.37(E)' },
          { value: 'license', label: 'Copy/proof of current license, registration, or certification - 8.370.22.37(F)' },
        ],
        required: true,
        helpText: 'Per 8.370.22.37 NMAC: Staff records must be available within 4 hours upon DHI request.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Staff records contain all required elements per 8.370.22.37 NMAC.',
      atRisk: 'Some staff records missing required elements.',
      nonCompliant: 'Staff records missing critical required elements.',
      unknown: 'Audit staff records against 8.370.22.37 NMAC.',
    },
    stateNotes: [
      'All 6 elements required per 8.370.22.37 NMAC',
      'Records must be available within 4 hours of DHI request',
      'Also required for volunteers with patient contact or working half-time+',
    ],
  },

  {
    id: 'NM-B3',
    itemId: 'B3',
    stateCode: 'NM',
    title: 'Professional Licensure Verification',
    description: 'New Mexico requires verification of all professional licenses per 8.370.22.23 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.23',
    regulationName: 'Licenses',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify and maintain proof of current NM license/registration/certification for ALL professional staff?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, verify before hire and track renewals' },
          { value: 'hire_only', label: 'Verify at hire only, don\'t track renewals' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
        helpText: 'Per 8.370.22.23 NMAC: Proof of licensure must be maintained on file.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional licensure verification is comprehensive per 8.370.22.23 NMAC.',
      atRisk: 'Track license renewals to prevent staff working with expired credentials.',
      nonCompliant: 'No license verification process violates 8.370.22.23 NMAC.',
      unknown: 'Verify professional license verification procedures.',
    },
    stateNotes: [
      'Applies to: physicians, PAs, NPs, PTs, OTs, SLPs, RNs, LPNs, social workers, PT/OT assistants',
      'Proof of current licensure must be on file',
      'Track renewal dates to prevent lapses',
    ],
  },

  {
    id: 'NM-B4',
    itemId: 'B4',
    stateCode: 'NM',
    title: 'Employee Abuse Registry Verification',
    description: 'New Mexico requires Employee Abuse Registry verification per 8.370.8 NMAC',
    severity: 'high',
    regulationCode: '8.370.8',
    regulationName: 'Abuse Registry',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify ALL employees against the NM Employee Abuse Registry before hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verify before hire' },
          { value: 'sometimes', label: 'Sometimes, but not consistently' },
          { value: 'never', label: 'No / not aware of requirement' },
        ],
        required: true,
        helpText: 'Per 8.370.8 NMAC: Cannot employ individuals listed on the Employee Abuse Registry.',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', value: 'never', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Employee Abuse Registry verification performed consistently per 8.370.8 NMAC.',
      atRisk: 'Registry verification is not consistent. Verify all employees before hire.',
      nonCompliant: 'Failure to verify the Employee Abuse Registry violates 8.370.8 NMAC.',
      unknown: 'Contact DHI at 1-800-752-8649 for registry verification procedures.',
    },
    stateNotes: [
      'Check before employment',
      'Cannot employ individuals on registry',
      'Document verification in personnel file',
    ],
  },

  {
    id: 'NM-B5',
    itemId: 'B5',
    stateCode: 'NM',
    title: 'Health and Age Requirements',
    description: 'New Mexico requires staff health screening and minimum age per 8.370.22.22 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.22',
    regulationName: 'Staff Health',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Are ALL staff involved in patient care at least 18 years of age?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Per 8.370.22.22(A) NMAC: All staff or contracted personnel in patient care must be at least 18.',
      },
      {
        id: 'q2',
        question: 'Do ALL staff with patient contact have current TB test per infectious disease bureau requirements?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have current TB documentation' },
          { value: 'most', label: 'Most do, some may be missing' },
          { value: 'no', label: 'No / not tracking' },
        ],
        required: true,
        helpText: 'Per 8.370.22.22(B) NMAC: TB test required per infectious disease bureau requirements.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'most', result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Health and age requirements met per 8.370.22.22 NMAC.',
      atRisk: 'Some TB screening documentation may be missing.',
      nonCompliant: 'Staff under 18 or missing TB screening violates 8.370.22.22 NMAC.',
      unknown: 'Verify all staff meet health and age requirements.',
    },
    stateNotes: [
      'Minimum age 18 for patient care staff',
      'TB test per infectious disease bureau requirements',
      'Also applies to contracted personnel and volunteers',
    ],
  },

  // ============================================
  // SECTION C: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'NM-C1',
    itemId: 'C1',
    stateCode: 'NM',
    title: 'Home Health Aide Training Requirements',
    description: 'New Mexico requires 75 hours HHA training per 8.370.22.30 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.30',
    regulationName: 'HHA Training',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL home health aides meet training/competency requirements per 8.370.22.30 NMAC?',
        inputType: 'checklist',
        options: [
          { value: '75_hours', label: '75 hours total training (classroom + supervised practical)' },
          { value: '16_practical', label: 'At least 16 hours supervised practical training' },
          { value: '16_classroom', label: '16 hours classroom before practical training' },
          { value: 'competency', label: 'Written test and skills demonstration completed' },
          { value: 'rn_supervised', label: 'Training by/under supervision of RN with 2 years experience (1 year home health)' },
        ],
        required: true,
        helpText: 'Per 8.370.22.30(C) NMAC: 75 hours minimum with at least 16 hours supervised practical.',
      },
      {
        id: 'q2',
        question: 'If providing HHA training, is your curriculum approved by DHI?',
        inputType: 'select',
        options: [
          { value: 'approved', label: 'Yes, DHI-approved curriculum' },
          { value: 'competency_only', label: 'Do not train, competency evaluation only' },
          { value: 'not_approved', label: 'Curriculum not yet approved' },
          { value: 'na', label: 'N/A - do not employ HHAs' },
        ],
        helpText: 'Per 8.370.22.30(B)(1) NMAC: Training curriculum must be approved before instituting training.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'na', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'includes', questionId: 'q2', value: ['approved', 'competency_only'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA training program meets 8.370.22.30 NMAC requirements.',
      atRisk: 'HHA training program may be incomplete.',
      nonCompliant: 'HHAs working without required training violates 8.370.22.30 NMAC.',
      unknown: 'Review HHA training against 8.370.22.30 NMAC.',
    },
    stateNotes: [
      '75 hours total (classroom + practical)',
      '16 hours supervised practical minimum',
      '16 hours classroom before practical',
      'Trainer: RN with 2 years nursing experience, 1 year home health',
      'Curriculum approval required before training',
    ],
  },

  {
    id: 'NM-C2',
    itemId: 'C2',
    stateCode: 'NM',
    title: 'Home Health Aide Annual In-Service and Performance Review',
    description: 'New Mexico requires 12 hours annual in-service per 8.370.22.30(F)-(G) NMAC',
    severity: 'high',
    regulationCode: '8.370.22.30(F)',
    regulationName: 'HHA In-Service',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL home health aides complete 12 hours of documented in-service training annually?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all complete 12+ hours annually' },
          { value: 'most', label: 'Most complete required hours' },
          { value: 'no', label: 'No structured annual training' },
          { value: 'na', label: 'N/A - do not employ HHAs' },
        ],
        required: true,
        helpText: 'Per 8.370.22.30(F) NMAC: 12 documented hours during each 12-month period.',
      },
      {
        id: 'q2',
        question: 'Do ALL home health aides receive annual performance review with written evaluation and skills demonstration?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all receive annual review' },
          { value: 'some', label: 'Some receive reviews' },
          { value: 'no', label: 'No formal annual review' },
          { value: 'na', label: 'N/A - do not employ HHAs' },
        ],
        helpText: 'Per 8.370.22.30(G) NMAC: Performance review no less than every 12 months.',
      },
    ],
    scoringRules: [
      { condition: 'includes', questionId: 'q1', value: ['na'], result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'some' },
      ], result: 'AT_RISK' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA annual in-service and performance reviews meet 8.370.22.30(F)-(G) requirements.',
      atRisk: 'Some HHAs may be behind on annual training or reviews.',
      nonCompliant: 'No structured annual training or review program violates 8.370.22.30 NMAC.',
      unknown: 'Review HHA annual requirements.',
    },
    stateNotes: [
      '12 hours in-service per 12-month period',
      'May be prorated in first year of employment',
      'Annual performance review required',
      'Review includes written evaluation AND skills demonstration',
    ],
  },

  {
    id: 'NM-C3',
    itemId: 'C3',
    stateCode: 'NM',
    title: 'Homemaker/PCA Training Requirements',
    description: 'New Mexico requires 40 hours training per 8.370.22.31 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.31',
    regulationName: 'PCA Training',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL homemakers/PCAs meet 8.370.22.31 NMAC training requirements?',
        inputType: 'checklist',
        options: [
          { value: '40_hours', label: '40 hours training in first year' },
          { value: '10_before', label: '10 hours completed before placing in patient home (8 service-specific + 2 orientation)' },
          { value: 'competency', label: 'Competency demonstrated and documented' },
          { value: 'curriculum', label: 'DHI-approved curriculum (if providing training)' },
        ],
        required: true,
        helpText: 'Per 8.370.22.31(C) NMAC: 40 hours first year, 10 hours before patient placement.',
      },
      {
        id: 'q2',
        question: 'Do ALL homemakers/PCAs complete 10 hours annual in-service?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all complete 10+ hours annually' },
          { value: 'most', label: 'Most complete required hours' },
          { value: 'no', label: 'No structured annual training' },
          { value: 'na', label: 'N/A - do not employ homemakers/PCAs' },
        ],
        helpText: 'Per 8.370.22.31(E)(3) NMAC: 10 documented hours during each 12-month period.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'na', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'all' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Homemaker/PCA training meets 8.370.22.31 NMAC requirements.',
      atRisk: 'Homemaker/PCA training may be incomplete.',
      nonCompliant: 'Homemakers/PCAs working without required training.',
      unknown: 'Review homemaker/PCA training requirements.',
    },
    stateNotes: [
      '40 hours first year',
      '10 hours before patient placement (8 service-specific + 2 orientation)',
      '10 hours annual in-service',
      'Training curriculum approval required if providing training',
    ],
  },

  // ============================================
  // SECTION D: SUPERVISION
  // ============================================
  {
    id: 'NM-D1',
    itemId: 'D1',
    stateCode: 'NM',
    title: 'Home Health Aide Supervision',
    description: 'New Mexico requires supervisory visits every 62 days per 8.370.22.29(D) NMAC',
    severity: 'high',
    regulationCode: '8.370.22.29(D)',
    regulationName: 'HHA Supervision',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do licensed professionals conduct supervisory visits to patient homes for HHAs?',
        inputType: 'select',
        options: [
          { value: 'every_62', label: 'Yes, at least every 62 days' },
          { value: 'irregular', label: 'Yes, but not always on schedule' },
          { value: 'no', label: 'No regular supervisory visits' },
          { value: 'na', label: 'N/A - do not employ HHAs' },
        ],
        required: true,
        helpText: 'Per 8.370.22.29(D)(2) NMAC: Supervisory visit at least every 62 days or as condition requires.',
      },
      {
        id: 'q2',
        question: 'Does supervision include written patient instructions identifying tasks for the HHA?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'Per 8.370.22.29(D)(1) NMAC: Written patient instructions required.',
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'na',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'na', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'every_62' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'irregular', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA supervision meets 8.370.22.29(D) NMAC requirements.',
      atRisk: 'Supervisory visits not consistently every 62 days.',
      nonCompliant: 'HHAs not receiving required supervision violates 8.370.22.29(D) NMAC.',
      unknown: 'Verify HHA supervision schedule.',
    },
    stateNotes: [
      'Supervision by RN, PT, OT, or SLP with 1 year experience',
      'Supervisory visit at least every 62 days',
      'Written patient instructions required',
      'HHA need not be present during supervisory visit',
    ],
  },

  {
    id: 'NM-D2',
    itemId: 'D2',
    stateCode: 'NM',
    title: 'LPN Supervision',
    description: 'New Mexico requires LPN supervision by RN per 8.370.22.29(A) NMAC',
    severity: 'high',
    regulationCode: '8.370.22.29(A)',
    regulationName: 'LPN Supervision',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do you employ or contract LPNs to provide patient care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are LPNs supervised by RNs with required qualifications and frequency?',
        inputType: 'checklist',
        options: [
          { value: 'rn_qualified', label: 'Supervising RN has 1 year home health OR 2 years nursing experience' },
          { value: 'tasks', label: 'RN identifies appropriate tasks for LPN' },
          { value: 'visits', label: 'Documented supervisory visit every 60 days or as indicated' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
        helpText: 'Per 8.370.22.29(A) NMAC: LPN supervision by RN with 1 year home health or 2 years nursing.',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'LPN supervision meets 8.370.22.29(A) NMAC requirements.',
      atRisk: 'LPN supervision may not meet all requirements.',
      nonCompliant: 'LPNs not receiving required RN supervision.',
      unknown: 'Verify LPN supervision requirements.',
    },
    stateNotes: [
      'Supervising RN: 1 year home health OR 2 years nursing experience',
      'Supervisory visit at least every 60 days',
      'Document supervisory visits',
    ],
  },

  {
    id: 'NM-D3',
    itemId: 'D3',
    stateCode: 'NM',
    title: 'Homemaker/PCA Supervision',
    description: 'New Mexico requires supervision of homemakers/PCAs per 8.370.22.29(E)-(F) NMAC',
    severity: 'high',
    regulationCode: '8.370.22.29(E)',
    regulationName: 'PCA Supervision',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do you employ homemakers or PCAs?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Are homemakers/PCAs supervised per 8.370.22.29(E)-(F) NMAC requirements?',
        inputType: 'checklist',
        options: [
          { value: 'supervisor', label: 'Licensed professional or qualified staff with 1 year direct care experience' },
          { value: 'instructions', label: 'Written patient care instructions provided' },
          { value: 'visits', label: 'Documented supervisory visit every 62 days or as condition requires' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Homemaker/PCA supervision meets requirements.',
      atRisk: 'Homemaker/PCA supervision may not meet all requirements.',
      nonCompliant: 'Homemakers/PCAs not receiving required supervision.',
      unknown: 'Verify homemaker/PCA supervision requirements.',
    },
    stateNotes: [
      'Supervisor: licensed professional or 1 year direct care experience',
      'Supervisory visit at least every 62 days',
      'Written patient care instructions required',
      'Staff need not be present during supervisory visit',
    ],
  },

  // ============================================
  // SECTION E: CARE DELIVERY
  // ============================================
  {
    id: 'NM-E1',
    itemId: 'E1',
    stateCode: 'NM',
    title: 'Plan of Care Requirements',
    description: 'New Mexico requires written plan of care per 8.370.22.33 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.33',
    regulationName: 'Plan of Care',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL patients have written plans of care reviewed at least annually?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, all patients before services begin' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
        helpText: 'Per 8.370.22.33 NMAC: Written plan of care reviewed at least annually.',
      },
      {
        id: 'q2',
        question: 'For medically directed care, do plans include all required elements per 8.370.22.33(A)?',
        inputType: 'checklist',
        options: [
          { value: 'physician', label: 'Established and periodically reviewed by physician' },
          { value: 'diagnoses', label: 'All pertinent diagnoses' },
          { value: 'mental', label: 'Mental status' },
          { value: 'services', label: 'Types of services and equipment' },
          { value: 'frequency', label: 'Frequency and duration of visits' },
          { value: 'limitations', label: 'Functional limitations' },
          { value: 'activities', label: 'Activities permitted' },
          { value: 'nutrition', label: 'Nutritional requirements' },
          { value: 'medications', label: 'Medications and treatments' },
          { value: 'safety', label: 'Safety measures' },
          { value: 'goals', label: 'Plans or goals for care' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 9 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Plans of care meet 8.370.22.33 NMAC requirements.',
      atRisk: 'Plans of care may be incomplete or not obtained timely.',
      nonCompliant: 'Services provided without proper plans of care violates 8.370.22.33 NMAC.',
      unknown: 'Review care plan process against requirements.',
    },
    stateNotes: [
      'Written plan of care required',
      'Reviewed at least annually',
      'Medically directed: established/reviewed by physician',
      'Non-medically directed: established/reviewed by agency staff annually',
    ],
  },

  {
    id: 'NM-E2',
    itemId: 'E2',
    stateCode: 'NM',
    title: 'Patient Rights',
    description: 'New Mexico requires protecting patient rights per 8.370.22.32 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.32',
    regulationName: 'Rights',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide patients with written patient rights per 8.370.22.32 NMAC, including:',
        inputType: 'checklist',
        options: [
          { value: 'informed', label: 'Right to be informed about care/treatment - (A)' },
          { value: 'refuse', label: 'Right to refuse or terminate treatment - (B)' },
          { value: 'changes', label: 'Right to be informed of changes affecting wellbeing - (C)' },
          { value: 'participate', label: 'Right to participate in planning care - (D)' },
          { value: 'dignity', label: 'Right to dignity, free from abuse/neglect/exploitation - (E)' },
          { value: 'grievance', label: 'Right to voice grievances without reprisal - (F)' },
          { value: 'confidentiality', label: 'Right to confidentiality - (G)' },
          { value: 'property', label: 'Right to have property treated with respect - (H)' },
          { value: 'charges', label: 'Right to be informed of charges orally and in writing - (I)' },
          { value: 'hotline', label: 'DHI hotline number: 1-800-752-8649 - (J)' },
          { value: 'advance_directive', label: 'Right to advance directive information - (K)' },
          { value: 'written', label: 'Right to receive written rights - (L)' },
        ],
        required: true,
        helpText: 'Per 8.370.22.32 NMAC: All listed patient rights must be protected.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 12, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 9, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 9, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights documentation meets 8.370.22.32 NMAC requirements.',
      atRisk: 'Patient rights documentation may be incomplete.',
      nonCompliant: 'Patient rights not fully protected per 8.370.22.32 NMAC.',
      unknown: 'Review patient rights documentation.',
    },
    stateNotes: [
      'All 12 rights (A-L) must be protected',
      'Provide written rights information',
      'Include DHI hotline: 1-800-752-8649',
      'Include advance directive information',
    ],
  },

  {
    id: 'NM-E3',
    itemId: 'E3',
    stateCode: 'NM',
    title: 'Patient Record Requirements',
    description: 'New Mexico requires specific patient record elements per 8.370.22.34 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.34',
    regulationName: 'Records',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'For medically directed patients, do records include all 8.370.22.34(A)(1) elements?',
        inputType: 'checklist',
        options: [
          { value: 'medical', label: 'Past and current medical findings - (a)' },
          { value: 'poc', label: 'Plan of care - (b)' },
          { value: 'id', label: 'Identifying information - (c)' },
          { value: 'physician', label: 'Name of physician - (d)' },
          { value: 'orders', label: 'Medications, diet, treatment/service, activity orders - (e)' },
          { value: 'notes', label: 'Signed and dated notes on day of service - (f)' },
          { value: 'summaries', label: 'Copies of summary reports to physician - (g)' },
          { value: 'rights', label: 'Evidence patient informed of rights - (h)' },
          { value: 'coordination', label: 'Evidence of care coordination - (i)' },
          { value: 'discharge', label: 'Discharge summary - (j)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient records contain all required elements per 8.370.22.34 NMAC.',
      atRisk: 'Patient records may be missing required elements.',
      nonCompliant: 'Patient records are incomplete.',
      unknown: 'Audit patient records against requirements.',
    },
    stateNotes: [
      'Records must be accurate, legible, promptly completed, consistently organized',
      'Available to DHI upon request',
      'Protected against loss or unauthorized use',
      '10-year retention after discharge',
    ],
  },

  // ============================================
  // SECTION F: QUALITY & COMPLIANCE
  // ============================================
  {
    id: 'NM-F1',
    itemId: 'F1',
    stateCode: 'NM',
    title: 'Quality Improvement Program',
    description: 'New Mexico requires ongoing QI program per 8.370.22.39 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.39',
    regulationName: 'QI Program',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Does your QI program document quarterly activity addressing all 8.370.22.39 NMAC elements?',
        inputType: 'checklist',
        options: [
          { value: 'clinical', label: 'Clinical care assessment (diagnoses, POC, services, standards) - (A)' },
          { value: 'operational', label: 'Operational activities (policies, statistics, complaints, staff utilization) - (B)' },
          { value: 'action_plan', label: 'Written QI action plan for identified problems - (C)' },
          { value: 'annual_report', label: 'Annual report reviewed by governing body and advisory group - (D)' },
        ],
        required: true,
        helpText: 'Per 8.370.22.39 NMAC: Quarterly documented activity required.',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 4, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 2, result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Quality improvement program meets 8.370.22.39 NMAC requirements.',
      atRisk: 'QI program needs additional components.',
      nonCompliant: 'QI program does not meet 8.370.22.39 NMAC requirements.',
      unknown: 'Review QI program against requirements.',
    },
    stateNotes: [
      'Ongoing QI program required',
      'Quarterly documented activity',
      'Annual report to governing body and advisory group',
      'No more than 1 year between evaluations of same part',
      'DHI may request quarterly summaries',
    ],
  },

  {
    id: 'NM-F2',
    itemId: 'F2',
    stateCode: 'NM',
    title: 'Complaint Handling',
    description: 'New Mexico requires complaint investigation within 3 working days per 8.370.22.40 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.40',
    regulationName: 'Complaints',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do you investigate complaints within 3 working days per 8.370.22.40 NMAC?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within 3 working days' },
          { value: 'usually', label: 'Usually, but sometimes delayed' },
          { value: 'no', label: 'No formal complaint process' },
        ],
        required: true,
        helpText: 'Per 8.370.22.40 NMAC: Investigation must be initiated within 3 working days.',
      },
      {
        id: 'q2',
        question: 'Do you document both the complaint and its resolution?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Complaint handling meets 8.370.22.40 NMAC requirements.',
      atRisk: 'Complaints not always investigated within 3 working days.',
      nonCompliant: 'No formal complaint handling process.',
      unknown: 'Review complaint procedures.',
    },
    stateNotes: [
      'Investigate within 3 working days',
      'Document complaint AND resolution',
      'Complaints may come from patient, caregiver, or guardian',
    ],
  },

  {
    id: 'NM-F3',
    itemId: 'F3',
    stateCode: 'NM',
    title: 'Incident Reporting',
    description: 'New Mexico requires incident reporting and documentation per 8.370.22.41 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.41',
    regulationName: 'Incidents',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do you report serious incidents and abuse/neglect/exploitation to DHI per 8.370.22.41(A) NMAC?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always report to DHI' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document all incidents within 5 days per 8.370.22.41(B) NMAC, including:',
        inputType: 'checklist',
        options: [
          { value: 'narrative', label: 'Narrative description of incident - (1)' },
          { value: 'contact', label: 'Evidence of DHI contact - (2)' },
          { value: 'investigation', label: 'Results of facility investigation - (3)' },
          { value: 'action', label: 'Facility action taken - (4)' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Incident reporting meets 8.370.22.41 NMAC requirements.',
      atRisk: 'Incident reporting may be inconsistent.',
      nonCompliant: 'Incidents not being reported to DHI as required.',
      unknown: 'Review incident reporting requirements.',
    },
    stateNotes: [
      'Report serious incidents threatening health/safety to DHI',
      'Report abuse, neglect, exploitation',
      'Document within 5 days of incident',
      'Documentation includes narrative, DHI contact, investigation, action taken',
    ],
  },

  {
    id: 'NM-F4',
    itemId: 'F4',
    stateCode: 'NM',
    title: 'Survey Readiness and Plan of Correction',
    description: 'New Mexico requires survey compliance per 8.370.22.19 NMAC',
    severity: 'high',
    regulationCode: '8.370.22.19',
    regulationName: 'Surveys',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'When was your last DHI survey?',
        inputType: 'select',
        options: [
          { value: 'within_year', label: 'Within the past year' },
          { value: 'over_year', label: 'More than 1 year ago' },
          { value: 'never', label: 'Never / New agency' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'If deficiencies were cited, did you submit POC within 10 working days per 8.370.22.19(C)?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, POC submitted on time' },
          { value: 'late', label: 'POC submitted late' },
          { value: 'none', label: 'No deficiencies cited' },
          { value: 'na', label: 'N/A - never surveyed' },
        ],
        helpText: 'Per 8.370.22.19(C) NMAC: Plan of correction due within 10 working days.',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'within_year' },
        { condition: 'includes', questionId: 'q2', value: ['yes', 'none'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'late', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'never', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Survey compliance satisfactory.',
      atRisk: 'Ensure POC submitted within 10 working days per 8.370.22.19(C).',
      nonCompliant: 'Survey compliance issues require attention.',
      unknown: 'Contact DHI to verify survey status.',
    },
    stateNotes: [
      'DHI may conduct unannounced surveys per 8.370.22.19(B)',
      'POC due within 10 working days of deficiency statement per 8.370.22.19(C)',
      'DHI may accept or require modifications to POC per 8.370.22.19(D)',
      'Keep copy of last survey on file per 8.370.22.35(A)',
    ],
  },

  // ============================================
  // SECTION G: CONTRACTED SERVICES & RECORDS
  // ============================================
  {
    id: 'NM-G1',
    itemId: 'G1',
    stateCode: 'NM',
    title: 'Contracted Services Requirements',
    description: 'New Mexico requires written contracts for arranged services per 8.370.22.36 NMAC',
    severity: 'medium',
    regulationCode: '8.370.22.36',
    regulationName: 'Contracts',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide any services through arrangement or contract?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - all services by employees' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do ALL contracts include required elements per 8.370.22.36 NMAC?',
        inputType: 'checklist',
        options: [
          { value: 'admission', label: 'Patients accepted only by primary agency - (A)' },
          { value: 'services', label: 'Services to be furnished - (B)' },
          { value: 'policies', label: 'Conform to agency policies and personnel qualifications - (C)' },
          { value: 'poc_participation', label: 'Responsibility for developing plans of care - (D)' },
          { value: 'control', label: 'How services controlled, coordinated, evaluated - (E)' },
          { value: 'procedures', label: 'Procedures for notes, scheduling, patient evaluation - (F)' },
          { value: 'payment', label: 'Procedures for payment - (G)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Contracted services agreements meet 8.370.22.36 NMAC requirements.',
      atRisk: 'Contracts may be missing required elements.',
      nonCompliant: 'Contracts do not meet 8.370.22.36 NMAC requirements.',
      unknown: 'Review contracts against requirements.',
    },
    stateNotes: [
      'Written contract required for all arranged services',
      'All 7 elements (A-G) required',
      'Primary agency retains control and responsibility',
    ],
  },

  {
    id: 'NM-G2',
    itemId: 'G2',
    stateCode: 'NM',
    title: 'Records Required on File',
    description: 'New Mexico requires specific records on file per 8.370.22.35 NMAC',
    severity: 'medium',
    regulationCode: '8.370.22.35',
    regulationName: 'Agency Records',
    agencyName: 'New Mexico Health Care Authority, Division of Health Improvement',
    agencyAbbr: 'HCA/DHI',
    agencyPhone: '1-800-752-8649',
    agencyUrl: 'https://www.hca.nm.gov/division-of-health-improvement/',
    questions: [
      {
        id: 'q1',
        question: 'Which required records do you maintain on file per 8.370.22.35 NMAC?',
        inputType: 'checklist',
        options: [
          { value: 'survey', label: 'Copy of last DHI survey - (A)' },
          { value: 'regs', label: 'Copy of 8.370.22 NMAC regulations - (B)' },
          { value: 'contracts', label: 'Agreements/contracts for services - (C)' },
          { value: 'patient', label: 'Patient records - (D)' },
          { value: 'staff', label: 'Staff records - (E)' },
          { value: 'training', label: 'Training and in-service records - (F)' },
          { value: 'minutes', label: 'Advisory group and governing board minutes - (G)' },
          { value: 'qi', label: 'Quality improvement program records - (H)' },
          { value: 'grievances', label: 'Grievances and resolutions - (I)' },
          { value: 'pharmacy', label: 'State board of pharmacy certificates (if applicable) - (J)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Required records on file per 8.370.22.35 NMAC.',
      atRisk: 'Some required records may be missing.',
      nonCompliant: 'Multiple required records missing.',
      unknown: 'Audit records against 8.370.22.35 NMAC.',
    },
    stateNotes: [
      'All listed records must be on file',
      'Available for DHI review during surveys',
      'Keep copy of current regulations',
    ],
  },

  {
    id: 'NM-G3',
    itemId: 'G3',
    stateCode: 'NM',
    title: 'OIG/SAM Exclusion List Verification',
    description: 'Federal requirement to verify employees against OIG exclusion list',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'OIG Exclusions',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How often do you check the OIG Exclusion List for employees?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'Not checking' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you also check SAM.gov exclusions?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'monthly' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG/SAM exclusion verification performed regularly.',
      atRisk: 'Implement monthly OIG/SAM verification. At-hire only is insufficient.',
      nonCompliant: 'Employing excluded individuals can result in civil monetary penalties.',
      unknown: 'Verify OIG/SAM checking procedures.',
    },
    stateNotes: [
      'OIG recommends monthly verification',
      'SAM.gov verification also recommended',
      'Document all exclusion checks',
      'Required for Medicare/Medicaid participation',
    ],
  },

  {
    id: 'NM-G4',
    itemId: 'G4',
    stateCode: 'NM',
    title: 'Medicaid EVV Compliance',
    description: 'New Mexico requires EVV for Medicaid personal care services',
    severity: 'high',
    regulationCode: '21st Century Cures Act',
    regulationName: 'EVV',
    agencyName: 'New Mexico Human Services Department',
    agencyAbbr: 'HSD',
    agencyPhone: '(505) 827-3100',
    agencyUrl: 'https://www.hsd.state.nm.us/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Medicaid-funded personal care or home health services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay/Medicare only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you use state-approved EVV system for all Medicaid visits?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, fully implemented' },
          { value: 'partial', label: 'Yes, but not for all visits' },
          { value: 'no', label: 'No EVV in place' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'partial', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV compliance satisfactory.',
      atRisk: 'EVV not used for all Medicaid visits. Ensure 100% compliance.',
      nonCompliant: 'Medicaid services require EVV per federal law. Contact HSD.',
      unknown: 'Verify EVV requirements with HSD.',
    },
    stateNotes: [
      'Required by 21st Century Cures Act',
      'Must use state-approved EVV system',
      'Non-compliance may affect Medicaid reimbursement',
    ],
  },
];

export default NEWMEXICO_QUESTIONS;